import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useUsersApi } from '../services/usersApi'
import {
  UserIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'

export default function UserMenu() {
  const { user, logout } = useAuth0()
  const navigate = useNavigate()
  const usersApi = useUsersApi()

  // Fetch current user data to get custom avatar
  const { data: userData } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => usersApi.getCurrentUser(),
    enabled: !!user, // Only run query when Auth0 user is available
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  const handleLogout = async () => {
    const returnTo = import.meta.env.VITE_AUTH0_LOGOUT_REDIRECT_URI || window.location.origin
    await logout({ logoutParams: { returnTo } })
  }

  // Use custom avatar if available, otherwise fall back to Auth0 picture
  const avatarUrl = userData?.avatar || user?.picture

  return (
    <Menu as="div" className="relative inline-block text-left z-50">
      <div>
        <Menu.Button className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
          <span className="mr-2">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={user?.name || 'User avatar'}
                className="h-8 w-8 rounded-full object-cover"
                onError={(e) => {
                  // If custom avatar fails to load, fall back to Auth0 picture
                  if (userData?.avatar && e.currentTarget.src === userData.avatar) {
                    e.currentTarget.src = user?.picture || ''
                  }
                }}
              />
            ) : (
              <UserIcon className="h-8 w-8 p-1 rounded-full bg-gray-200 dark:bg-gray-600" />
            )}
          </span>
          <span>{(user as any).given_name || (user as any).name || (user as any).email}</span>
          <ChevronDownIcon className="w-4 h-4 ml-2" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Role</div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
              {(user && Array.isArray(user['https://midwestrailbirds.com/permissions']) && user['https://midwestrailbirds.com/permissions'].includes('admin')) ? 'Admin' : 'Member'}
            </div>
          </div>
          <div className="px-1 py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate('/profile')}
                  className={`${
                    active ? 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100' : 'text-gray-900 dark:text-gray-100'
                  } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                >
                  <UserIcon className="mr-2 h-5 w-5" />
                  View Profile
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate('/settings')}
                  className={`${
                    active ? 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100' : 'text-gray-900 dark:text-gray-100'
                  } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                >
                  <CogIcon className="mr-2 h-5 w-5" />
                  Settings
                </button>
              )}
            </Menu.Item>
          </div>
          <div className="px-1 py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={`${
                    active ? 'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-gray-100'
                  } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                >
                  <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5" />
                  Sign Out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
